/**
 * WeaveIn.ai Platform - External Systems API Service
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ExternalSystem {
  id: number;
  cbo_id: number;
  name: string;
  system_type: string;
  endpoint_url: string | null;
  description: string | null;
  auth_type: string;
  api_key_prefix: string | null;
  allowed_programs: number[];
  allowed_fields: string[];
  rate_limit_per_minute: number;
  status: string;
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
  revoked_at: string | null;
  recent_access?: AccessLogEntry[];
}

export interface ExternalSystemCreateResponse extends ExternalSystem {
  api_key: string;
}

export interface ExternalSystemRegisterRequest {
  name: string;
  system_type?: string;
  endpoint_url?: string;
  description?: string;
  allowed_programs?: number[];
  allowed_fields?: string[];
  rate_limit_per_minute?: number;
}

export interface ExternalSystemUpdateRequest {
  name?: string;
  system_type?: string;
  endpoint_url?: string;
  description?: string;
  allowed_programs?: number[];
  allowed_fields?: string[];
  rate_limit_per_minute?: number;
}

export interface AccessLogEntry {
  id: number;
  endpoint: string | null;
  method: string | null;
  status_code: number | null;
  response_time_ms: number | null;
  records_accessed: number;
  ip_address: string | null;
  error_message: string | null;
  accessed_at: string | null;
}

// ============================================================================
// API CLIENT
// ============================================================================

class ExternalSystemsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });
  }

  async registerSystem(
    cboId: number,
    data: ExternalSystemRegisterRequest
  ): Promise<ExternalSystemCreateResponse> {
    const response = await this.client.post<ExternalSystemCreateResponse>(
      `/api/organizations/${cboId}/external-systems`,
      data
    );
    return response.data;
  }

  async listSystems(cboId: number): Promise<ExternalSystem[]> {
    const response = await this.client.get<ExternalSystem[]>(
      `/api/organizations/${cboId}/external-systems`
    );
    return response.data;
  }

  async getSystem(cboId: number, systemId: number): Promise<ExternalSystem> {
    const response = await this.client.get<ExternalSystem>(
      `/api/organizations/${cboId}/external-systems/${systemId}`
    );
    return response.data;
  }

  async updateSystem(
    cboId: number,
    systemId: number,
    data: ExternalSystemUpdateRequest
  ): Promise<ExternalSystem> {
    const response = await this.client.put<ExternalSystem>(
      `/api/organizations/${cboId}/external-systems/${systemId}`,
      data
    );
    return response.data;
  }

  async revokeSystem(cboId: number, systemId: number): Promise<void> {
    await this.client.delete(
      `/api/organizations/${cboId}/external-systems/${systemId}`
    );
  }

  async rotateKey(cboId: number, systemId: number): Promise<ExternalSystemCreateResponse> {
    const response = await this.client.post<ExternalSystemCreateResponse>(
      `/api/organizations/${cboId}/external-systems/${systemId}/rotate-key`
    );
    return response.data;
  }
}

export const externalSystemsAPI = new ExternalSystemsAPI();
